import "./consensusQuestion.css"
import { H6 } from "@bootstrap-styled/v4/";
import { faCopy, faPlus, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import { AnswerOptions } from "./AnswerOptions";
import { Standards } from "./Standards";
import { PreviewConsensusQuestionModal } from "./PreviewConsensusQuestionModal";
import { MathJax } from "../MathJax";
import { validOpenResponseSolution } from "utils";

const OPTION_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

// Read-only display of distractor_rationales aligned to answer options by index.
// Defensive across three shapes:
//   - EEDI:        [{ explanation, misconception }, ...]
//   - Wren/legacy: ["...", ...]  (plain strings)
//   - null / missing / shorter-than-options: render nothing for that option.
// Explanation/option text may contain inline LaTeX (\( ... \)); render through
// the same MathJax path the rendering mode uses so fractions etc. display.
const DistractorRationales = ({ answerOptions, distractorRationales }) => {
  if (!Array.isArray(distractorRationales) || distractorRationales.length === 0) {
    return null;
  }
  const options = Array.isArray(answerOptions) ? answerOptions : [];
  const rows = options.map((option, i) => {
    const rationale = distractorRationales[i];
    if (rationale === null || rationale === undefined) return null;

    let misconception = null;
    let explanation = null;
    if (typeof rationale === "string") {
      explanation = rationale;
    } else if (typeof rationale === "object") {
      misconception = rationale.misconception ?? null;
      explanation = rationale.explanation ?? null;
    }
    if (!misconception && !explanation) return null;

    return (
      <div key={i} style={{ marginBottom: "8px" }}>
        <div style={{ fontWeight: 600, fontSize: "0.85em", color: "#333" }}>
          {OPTION_LETTERS[i] || i + 1}.{" "}
          {typeof option === "string" ? <MathJax expression={option} isMathJaxSet={false} /> : null}
        </div>
        {misconception && (
          <span
            style={{
              display: "inline-block",
              background: "#fdecec",
              color: "#a3261a",
              borderRadius: "10px",
              padding: "1px 8px",
              fontSize: "0.78em",
              fontWeight: 600,
              marginTop: "2px",
            }}
          >
            {misconception}
          </span>
        )}
        {explanation && (
          <div style={{ fontSize: "0.88em", color: "#444", marginTop: "2px" }}>
            <MathJax expression={explanation} isMathJaxSet={false} />
          </div>
        )}
      </div>
    );
  }).filter(Boolean);

  if (rows.length === 0) return null;

  return (
    <div style={{ marginTop: "10px" }}>
      <H6>Distractor rationales (read-only, from partner / importer)</H6>
      {rows}
    </div>
  );
};

const TextInput = (props) => {
  const { label, type, value, onChange, labelClassName, inputClassName, divclassName="challenge-prop horizontal",  ...rest } = props;
  return (
    <div className={divclassName}>
      <H6 className={labelClassName}>{label}</H6>
      <input
        className={inputClassName || "full-width round-box"}
        type={type}
        value={value}
        onChange={onChange}
        {...rest}
      />
    </div>
  );
};

const SelectInput = (props) => {
  const { label, id, value, options, onChange } = props;
  return (
    <div className="challenge-prop horizontal">
      <H6 className="header-margin-right">{label}</H6>
      <select id={id} name={id} value={value} onChange={onChange}>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

const TriBoolSelect = ({ label, value, onChange }) => {
  // value: null | true | false. Stored as the literal string so <select> can carry undefined.
  const stringValue = value === true ? "true" : value === false ? "false" : "";
  const handleChange = (e) => {
    const v = e.target.value;
    if (v === "true") onChange(true);
    else if (v === "false") onChange(false);
    else onChange(null);
  };
  return (
    <div className="challenge-prop horizontal">
      <H6 className="header-margin-right">{label}</H6>
      <select value={stringValue} onChange={handleChange}>
        <option value="">— unset —</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    </div>
  );
};

const CopyIconButton = ({ value, title = "Copy" }) => {
  // Sits inside the accordion header (which is a toggle), so stop propagation
  // to avoid expanding/collapsing the item when copying.
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value).then(() => {});
  };
  return (
    <FontAwesomeIcon
      icon={faCopy}
      title={title}
      onClick={handleCopy}
      style={{ cursor: "pointer", marginLeft: "6px", color: "#666", flexShrink: 0 }}
    />
  );
};

const StandardsBadges = ({ standards }) => {
  if (!standards || standards.length === 0) return null;
  return (
    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: "4px" }}>
      {standards.map((s) => (
        <span
          key={s}
          style={{
            background: "#e6f0ff",
            color: "#1a3a8a",
            borderRadius: "10px",
            padding: "2px 8px",
            fontSize: "0.85em",
            fontWeight: 500,
          }}
        >
          {s}
        </span>
      ))}
    </span>
  );
};

// Uppercase micro-label for the header's left column; fixed width so every
// value (ID, Status, Source, External ID) aligns on the same left edge.
const headerLabelStyle = {
  flexShrink: 0,
  width: "88px",
  textTransform: "uppercase",
  fontSize: "0.68em",
  fontWeight: 600,
  letterSpacing: "0.05em",
  color: "#8a8a8a",
};

const monoValueStyle = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  fontSize: "0.9em",
  wordBreak: "break-all",
};

const HeaderRow = ({ label, children }) => (
  <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
    <span style={headerLabelStyle}>{label}</span>
    <span style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", minWidth: 0 }}>
      {children}
    </span>
  </div>
);

const StatusPill = ({ status }) => {
  const isActive = String(status).toLowerCase() === "active";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontSize: "0.8em",
        fontWeight: 600,
        letterSpacing: "0.03em",
        color: isActive ? "#1a7a3a" : "#888",
      }}
    >
      <span
        style={{
          width: "7px",
          height: "7px",
          borderRadius: "50%",
          background: isActive ? "#22a356" : "#bbb",
        }}
      />
      {String(status).toUpperCase()}
    </span>
  );
};

// Generic colored pill used by the Quality Gate header/body (Pass/Reject/Not
// reviewed), styled consistently with StatusPill above.
const Pill = ({ label, color, dotColor }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "0.8em",
      fontWeight: 600,
      letterSpacing: "0.03em",
      color,
    }}
  >
    <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: dotColor }} />
    {label}
  </span>
);

const QualityGatePill = ({ qualityGate }) => {
  if (!qualityGate) {
    return <Pill label="NOT REVIEWED" color="#888" dotColor="#bbb" />;
  }
  const normalized = String(qualityGate).toLowerCase();
  if (normalized === "pass") return <Pill label="PASS" color="#1a7a3a" dotColor="#22a356" />;
  if (normalized === "reject") return <Pill label="REJECT" color="#a3261a" dotColor="#d63b2b" />;
  return <Pill label={String(qualityGate).toUpperCase()} color="#888" dotColor="#bbb" />;
};

// "Not reviewed" placeholder shared by the LC Tag Results and Quality Results
// panels when there's no evaluation row at all for the selected run.
const NotReviewedState = ({ label = "Not reviewed" }) => (
  <p style={{ color: "#888", fontStyle: "italic", margin: "4px 0" }}>{label}</p>
);

// A single label/value line inside the LC Tag Results / Quality Results
// panels, matching the Editorial metadata panel's row style.
const InfoRow = ({ label, children }) => (
  <div className="challenge-prop horizontal">
    <H6 className="header-margin-right">{label}</H6>
    <span>{children}</span>
  </div>
);

const NOT_YET_AVAILABLE = <span style={{ color: "#aaa", fontStyle: "italic" }}>Not yet available</span>;

const valueOrDash = (value) => (value === null || value === undefined || value === "" ? "—" : value);

// LC Tag Results table (OKO2-3551). Renders benchmark.lc_ratings in the order
// given by the server (Strong, Partial, Weak, No, then null-rating) — never
// re-sorted client-side.
const LcTagResultsTable = ({ lcRatings }) => {
  if (!Array.isArray(lcRatings) || lcRatings.length === 0) {
    return <NotReviewedState />;
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85em" }}>
        <thead>
          <tr>
            {["LC ID", "CCSS", "LC Name", "Alignment", "Rationale", "Prompt Version", "Model"].map((h) => (
              <th
                key={h}
                style={{
                  textAlign: "left",
                  borderBottom: "1px solid #ddd",
                  padding: "4px 8px",
                  color: "#666",
                  fontWeight: 600,
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lcRatings.map((r, i) => (
            <tr key={`${r.lc_id}-${i}`}>
              <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee", fontFamily: monoValueStyle.fontFamily }}>
                {valueOrDash(r.lc_id)}
              </td>
              <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee" }}>
                {valueOrDash(r.related_ccss_code)}
              </td>
              <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee" }}>
                {valueOrDash(r.lc_description)}
              </td>
              <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee" }}>
                {valueOrDash(r.rating_label)}
              </td>
              <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee" }}>
                {valueOrDash(r.rationale)}
              </td>
              <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee" }}>
                {valueOrDash(r.prompt_version)}
              </td>
              <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee" }}>
                {valueOrDash(r.model)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Quality Results panel (OKO2-3551). gate_reasoning and worked_solution are
// currently NULL on every row pending a data fill; render an explicit
// "Not yet available" state for them rather than hiding the fields.
const QualityResultsPanel = ({ qualityResult }) => {
  if (!qualityResult) {
    return <NotReviewedState />;
  }
  return (
    <div>
      <InfoRow label="Quality Gate">
        <QualityGatePill qualityGate={qualityResult.quality_gate} />
      </InfoRow>
      <InfoRow label="Reject Tags">{valueOrDash(qualityResult.rejection_tag)}</InfoRow>
      <InfoRow label="Gate Reasoning">
        {qualityResult.gate_reasoning ? qualityResult.gate_reasoning : NOT_YET_AVAILABLE}
      </InfoRow>
      <InfoRow label="Worked Solution">
        {qualityResult.worked_solution ? qualityResult.worked_solution : NOT_YET_AVAILABLE}
      </InfoRow>
      <InfoRow label="Soft Flag">{valueOrDash(qualityResult.soft_flag)}</InfoRow>
      <InfoRow label="Item Solve">{valueOrDash(qualityResult.item_solve)}</InfoRow>
      <InfoRow label="Prompt Version">{valueOrDash(qualityResult.prompt_version)}</InfoRow>
      <InfoRow label="Model">{valueOrDash(qualityResult.model)}</InfoRow>
    </div>
  );
};

// The prompt is stored as HTML (with inline LaTeX); show a clean plain-text
// preview in the collapsed header instead of raw <p> tags.
const stripHtmlPreview = (html, max = 120) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const text = (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

export const ConsensusQuestion = ({ challenge, index, showHeader, setNeedsRefresh }) => {
  const [prompt, setPrompt] = useState(challenge?.prompt);
  const [spokenPrompt, setSpokenPrompt] = useState(challenge?.spoken_prompt);
  const [difficulty, setDifficulty] = useState(challenge?.difficulty);
  const [solutionIndex, setSolutionIndex] = useState(challenge?.solution_index);
  const [answerOptions, setAnswerOptions] = useState(challenge?.answer_options);
  const [randomizeOptions, setRandomizeOptions] = useState(challenge?.randomize_options);
  const [promptImageUrl, setPromptImageUrl] = useState(challenge?.prompt_image_url);
  const [internalImageUrl] = useState(challenge?.internal_image_url);
  const [standards, setStandards] = useState(challenge?.standards ?? []);
  const [comments, setComments] = useState(challenge?.comments);
  const [renderType, setRenderType] = useState(challenge?.render_type);
  const [status, setStatus] = useState(challenge?.status ?? "active");
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [solution, setSolution] = useState(challenge?.solution);
  const [answerPrefix, setAnswerPrefix] = useState(challenge?.answer_prefix || "");
  const [answerSuffix, setAnswerSuffix] = useState(
    challenge?.answer_suffix || challenge?.answer_unit || ""
  );

  // Editorial metadata
  const [lcId, setLcId] = useState(challenge?.lc_id || "");
  const [imLessonId, setImLessonId] = useState(challenge?.im_lesson_id || "");
  const [wordProblem, setWordProblem] = useState(
    challenge?.word_problem === undefined ? null : challenge.word_problem,
  );
  const [equationCentered, setEquationCentered] = useState(
    challenge?.equation_centered === undefined ? null : challenge.equation_centered,
  );
  const [illustration, setIllustration] = useState(
    challenge?.illustration === undefined ? null : challenge.illustration,
  );
  // Read-only (D-Q1): distractor_rationales and illustration_taxonomy come from
  // the partner / importer and are not authored inline by reviewers in this slice.
  const illustrationTaxonomy = challenge?.illustration_taxonomy || null;
  const distractorRationales = challenge?.distractor_rationales || null;

  // LC benchmark data for the selected run (OKO2-3551). Absent/null benchmark,
  // or a benchmark with no quality_result and no lc_ratings, both mean "no
  // evaluation row for this run" — treated uniformly as "not reviewed".
  const benchmark = challenge?.benchmark || null;
  const lcRatings = benchmark?.lc_ratings || [];
  const qualityResult = benchmark?.quality_result || null;
  const isReviewed = !!benchmark && (lcRatings.length > 0 || !!qualityResult);
  const lcTagCount = isReviewed ? benchmark?.lc_tag_count ?? 0 : null;

  const mcMode = !!challenge?.answer_options;

  const renderTypeOptions = [
    { value: "fraction_legacy", label: "Fraction Legacy" },
    { value: "math", label: "Math" },
  ];

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  // The server keeps the formula text only while the image is the stored one.
  const keepsStoredImage = promptImageUrl === challenge?.prompt_image_url;

  const buildChallengeRequest = () => {
    const r = {};
    r.id = challenge.id;
    r.difficulty = difficulty;
    r.standards = standards;
    r.comments = comments;
    r.status = status;
    r.prompt = prompt;
    r.spoken_prompt = spokenPrompt;
    r.randomize_options = randomizeOptions === true;
    r.prompt_image_url = promptImageUrl;
    r.prompt_image_latex = keepsStoredImage ? challenge.prompt_image_latex ?? null : null;
    r.internal_image_url = internalImageUrl;
    r.render_type = renderType;
    r.solution_index = solutionIndex;
    r.answer_options = answerOptions;
    r.solution = solution;
    r.source = challenge.source;
    r.external_id = challenge.external_id;

    // Editorial metadata round-trip
    r.lc_id = lcId || null;
    r.im_lesson_id = imLessonId || null;
    r.word_problem = wordProblem;
    r.equation_centered = equationCentered;
    r.illustration = illustration;
    r.illustration_taxonomy = illustrationTaxonomy;
    if (mcMode) {
      r.distractor_rationales = distractorRationales;
    } else {
      r.answer_prefix = answerPrefix || null;
      r.answer_suffix = answerSuffix || null;
      r.answer_unit = answerSuffix || null;
    }
    return r;
  };

  const handlePreview = () => setIsPreviewModalOpen(true);
  const handlePrompt = (e) => setPrompt(e.target.value);
  const handleSpokenPrompt = (e) => setSpokenPrompt(e.target.value);
  const handleDifficulty = (e) => setDifficulty(e.target.value);
  const handleRandomizeOptions = (e) => setRandomizeOptions(e.target.checked);
  const handlePromptImageUrl = (e) => setPromptImageUrl(e.target.value);
  const handleComments = (e) => setComments(e.target.value);
  const handleRenderType = (e) => setRenderType(e.target.value);
  const handleStatus = (e) => setStatus(e.target.value);
  const handleSolution = (e) => {
    const value = e.target.value;
    if (validOpenResponseSolution(value)) {
      setSolution(value);
    }
  };
  const handleAnswerPrefix = (e) => setAnswerPrefix(e.target.value);
  const handleAnswerSuffix = (e) => setAnswerSuffix(e.target.value);

  const addAnswerOption = () => {
    const newAnswerOptions = JSON.parse(JSON.stringify(answerOptions));
    newAnswerOptions.push("");
    setAnswerOptions(newAnswerOptions);
  };

  const addStandard = () => {
    const newStandards = JSON.parse(JSON.stringify(standards));
    newStandards.push("");
    setStandards(newStandards);
  };

  const answerOptionsComp = (
    <div className="challenge-prop half-width">
      <AnswerOptions
        answerOptions={answerOptions}
        setAnswerOptions={setAnswerOptions}
        solutionIndex={solutionIndex}
        setSolutionIndex={setSolutionIndex}
      />
      {mcMode && (
        <DistractorRationales
          answerOptions={answerOptions}
          distractorRationales={distractorRationales}
        />
      )}
      <button
        style={{ borderRadius: "100px", borderWidth: "1px", marginLeft: "18px", marginTop: "8px" }}
        onClick={addAnswerOption}
      >
        <FontAwesomeIcon icon={faPlus} />
        &nbsp;Add answer option
      </button>
    </div>
  );

  return (
    <div className="multiple-choice-question">
      <Accordion.Item eventKey={index} key={index}>
        {showHeader && (
          <Accordion.Header>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "7px",
                width: "100%",
                minWidth: 0,
                textAlign: "left",
                paddingRight: "12px",
              }}
            >
              <HeaderRow label="ID">
                <span style={monoValueStyle}>{challenge.id}</span>
                <CopyIconButton value={challenge.id} title="Copy OKO question ID" />
              </HeaderRow>
              <HeaderRow label="Status">
                <StatusPill status={status} />
              </HeaderRow>
              <HeaderRow label="Source">
                <span style={{ wordBreak: "break-word" }}>{challenge.source || "—"}</span>
              </HeaderRow>
              {challenge.external_id && (
                <HeaderRow label="External ID">
                  <span style={monoValueStyle}>{challenge.external_id}</span>
                  <CopyIconButton
                    value={challenge.external_id}
                    title={`Copy ${challenge.source || "source"} external ID`}
                  />
                </HeaderRow>
              )}
              {standards.length > 0 && (
                <HeaderRow label="Standards">
                  <StandardsBadges standards={standards} />
                </HeaderRow>
              )}
              <HeaderRow label="LC Tag Count">
                <span>{lcTagCount === null ? "Not reviewed" : lcTagCount}</span>
              </HeaderRow>
              <HeaderRow label="Quality Gate">
                <QualityGatePill qualityGate={qualityResult?.quality_gate} />
              </HeaderRow>
              {prompt && (
                <div style={{ color: "#666", fontSize: "0.9em", lineHeight: 1.4 }}>
                  {stripHtmlPreview(prompt)}
                </div>
              )}
            </div>
          </Accordion.Header>
        )}
        <Accordion.Body>
          <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <div>
              <TextInput
                label="Prompt"
                divclassName="challenge-prop"
                type="text"
                value={prompt}
                onChange={handlePrompt}
              />
              <TextInput
                label="Spoken Prompt"
                divclassName="challenge-prop"
                type="text"
                value={spokenPrompt}
                onChange={handleSpokenPrompt}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "row" }}>
              {mcMode ? (
                answerOptionsComp
              ) : (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <TextInput
                    label="Solution"
                    divclassName="challenge-prop"
                    type="text"
                    value={solution}
                    onChange={handleSolution}
                  />
                  <TextInput
                    label="Answer prefix"
                    divclassName="challenge-prop"
                    type="text"
                    value={answerPrefix}
                    onChange={handleAnswerPrefix}
                  />
                  <TextInput
                    label="Answer suffix"
                    divclassName="challenge-prop"
                    type="text"
                    value={answerSuffix}
                    onChange={handleAnswerSuffix}
                  />
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "row" }}>
                <div className="challenge-prop">
                  <Standards standards={standards} setStandards={setStandards} />
                  <button className="round-box" onClick={addStandard}>
                    <FontAwesomeIcon icon={faPlus} />
                    &nbsp;Add standard
                  </button>
                </div>
              </div>
              <div
                style={{
                  marginLeft: "15px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <TextInput
                  label="Difficulty"
                  labelClassName="header-margin-right"
                  inputClassName="input-number round-box"
                  type="number"
                  value={difficulty}
                  onChange={handleDifficulty}
                  min="1"
                  max="5"
                />

                {mcMode && (
                  <div className="challenge-prop horizontal">
                    <H6 className="header-margin-right">Randomize Options</H6>
                    <input
                      type="checkbox"
                      checked={randomizeOptions === true}
                      onClick={handleRandomizeOptions}
                    />
                  </div>
                )}

                <SelectInput
                  label="Render Type"
                  id="renderType"
                  value={renderType}
                  onChange={handleRenderType}
                  options={renderTypeOptions}
                />

                <SelectInput
                  label="Status"
                  id="status"
                  value={status}
                  onChange={handleStatus}
                  options={statusOptions}
                />
              </div>
            </div>
            <TextInput
              divclassName="challenge-prop horizontal"
              labelClassName="image-url-width"
              label="Prompt Image URL"
              type="url"
              value={promptImageUrl}
              onChange={handlePromptImageUrl}
            />
            <TextInput
              divclassName="challenge-prop horizontal"
              labelClassName="image-url-width"
              label="Internal Image URL"
              type="url"
              value={internalImageUrl}
              readOnly
              disabled
            />
            {(internalImageUrl || promptImageUrl) && (
              <div className="challenge-prop horizontal">
                <img
                  src={internalImageUrl || promptImageUrl}
                  alt="challenge stimulus"
                  style={{
                    maxWidth: "320px",
                    maxHeight: "320px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    padding: "4px",
                  }}
                />
              </div>
            )}
            <TextInput
              divclassName="challenge-prop horizontal"
              labelClassName="header-margin-right"
              label="Comments"
              type="text"
              value={comments}
              onChange={handleComments}
            />

            {/* Editorial metadata */}
            <div
              style={{
                marginTop: "16px",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                background: "#fafbfc",
              }}
            >
              <H6 style={{ marginTop: 0 }}>Editorial metadata</H6>
              <TextInput
                divclassName="challenge-prop horizontal"
                labelClassName="header-margin-right"
                label="LC ID"
                type="text"
                value={lcId}
                onChange={(e) => setLcId(e.target.value)}
              />
              <TextInput
                divclassName="challenge-prop horizontal"
                labelClassName="header-margin-right"
                label="IM Lesson ID"
                type="text"
                value={imLessonId}
                onChange={(e) => setImLessonId(e.target.value)}
              />
              <TriBoolSelect label="Word problem?" value={wordProblem} onChange={setWordProblem} />
              <TriBoolSelect label="Equation-centered?" value={equationCentered} onChange={setEquationCentered} />
              <TriBoolSelect label="Has illustration?" value={illustration} onChange={setIllustration} />
              {illustrationTaxonomy && (
                <div className="challenge-prop horizontal">
                  <H6 className="header-margin-right">Illustration taxonomy (read-only)</H6>
                  <code style={{ fontSize: "0.85em", color: "#444" }}>
                    {JSON.stringify(illustrationTaxonomy)}
                  </code>
                </div>
              )}
            </div>

            {/* LC Tag Results (OKO2-3551) */}
            <div
              style={{
                marginTop: "16px",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                background: "#fafbfc",
              }}
            >
              <H6 style={{ marginTop: 0 }}>LC Tag Results</H6>
              <LcTagResultsTable lcRatings={lcRatings} />
            </div>

            {/* Quality Results (OKO2-3551) */}
            <div
              style={{
                marginTop: "16px",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                background: "#fafbfc",
              }}
            >
              <H6 style={{ marginTop: 0 }}>Quality Results</H6>
              <QualityResultsPanel qualityResult={qualityResult} />
            </div>
          </div>
          <div className="preview-button-div">
            <button className="preview-button round-box" onClick={handlePreview}>
              <FontAwesomeIcon icon={faSearch} />
              &nbsp;Preview challenge in rendering mode and save changes
            </button>
          </div>
        </Accordion.Body>
      </Accordion.Item>
      <PreviewConsensusQuestionModal
        challenge={buildChallengeRequest()}
        isModalOpen={isPreviewModalOpen}
        setIsModalOpen={setIsPreviewModalOpen}
        setNeedsRefresh={setNeedsRefresh}
      />
    </div>
  );
};
